/*
    This file uses code from @react-three/drei.
    Original Copyright (c) 2020 react-spring
    Source: https://github.com/pmndrs/drei
    License: MIT (see Frontend/src/THIRD-PARTY-LICENSES/drei-MIT.txt)
*/

import { Html, Line } from '@react-three/drei';
import { type ThreeEvent, useThree } from '@react-three/fiber';
import { FC, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { DoubleSide, type Group, Matrix4, Quaternion, Vector3 } from 'three';

import { context } from 'app/modules/wallforming/components/scenes/tools/pivot-controls/context';

const vec1 = /* @__PURE__ */ new Vector3();
const vec2 = /* @__PURE__ */ new Vector3();

export const calculateOffset = (
    clickPoint: Vector3,
    normal: Vector3,
    rayStart: Vector3,
    rayDir: Vector3
) => {
    const e1 = normal.dot(normal);
    const e2 = normal.dot(clickPoint) - normal.dot(rayStart);
    const e3 = normal.dot(rayDir);

    if (e3 === 0) {
        return -e2 / e1;
    }

    vec1.copy(rayDir)
        .multiplyScalar(e1 / e3)
        .sub(normal);
    vec2.copy(rayDir)
        .multiplyScalar(e2 / e3)
        .add(rayStart)
        .sub(clickPoint);

    const offset = -vec1.dot(vec2) / vec1.dot(vec1);
    return offset;
};

const upV = /* @__PURE__ */ new Vector3(0, 1, 0);
const offsetMatrix = /* @__PURE__ */ new Matrix4();

export const AxisArrow: FC<{ direction: Vector3; axis: 0 | 1 | 2 }> = ({ direction, axis }) => {
    const {
        translation,
        translationLimits,
        annotations,
        annotationsClass,
        depthTest,
        scale,
        lineWidth,
        fixed,
        axisColors,
        hoveredColor,
        selectedColor,
        renderOrder,
        onDragStart,
        onDrag,
        onDragEnd,
        userData,
        selectedControl,
        setSelectedControl
    } = useContext(context);

    const gl = useThree((state) => state.gl);
    const camControls = useThree((state) => state.controls) as unknown as { enabled: boolean } | undefined;
    const camControlsRef = useRef(camControls);
    const divRef = useRef<HTMLDivElement>(null!);
    const objRef = useRef<Group>(null!);
    const clickInfo = useRef<{ clickPoint: Vector3; dir: Vector3 } | null>(null);
    const offset0 = useRef<number>(0);
    const translationRef = useRef<[number, number, number]>(translation.current);
    const [isHovered, setIsHovered] = useState(false);

    const isSelected = selectedControl?.component === 'Arrow' && selectedControl?.axis === axis;
    const color = isHovered
        ? (hoveredColor ?? axisColors[axis])
        : selectedControl
          ? isSelected
              ? (selectedColor ?? axisColors[axis])
              : '#696969'
          : axisColors[axis];

    const opacity = isSelected || isHovered ? 1 : 0.6;

    const onPointerDown = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            setSelectedControl({ component: 'Arrow', axis });
            if (annotations) {
                divRef.current.innerText = `${translationRef.current[axis].toFixed(2)}`;
                divRef.current.style.display = 'block';
            }
            e.stopPropagation();
            const rotation = new Matrix4().extractRotation(objRef.current.matrixWorld);
            const clickPoint = e.point.clone();
            const origin = new Vector3().setFromMatrixPosition(objRef.current.matrixWorld);
            const dir = direction.clone().applyMatrix4(rotation).normalize();
            clickInfo.current = { clickPoint, dir };
            offset0.current = translationRef.current[axis];
            onDragStart({ component: 'Arrow', axis, origin, directions: [dir] });
            camControls && camControlsRef.current && (camControlsRef.current.enabled = false);

            // @ts-expect-error - releasePointerCapture & PointerEvent#pointerId is not in the type definition
            e.target.setPointerCapture(e.pointerId);
        },
        [annotations, axis, camControls, direction, onDragStart, setSelectedControl]
    );

    const onPointerMove = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            e.stopPropagation();
            gl.domElement.style.setProperty('cursor', 'pointer');
            if (!isHovered) {
                setIsHovered(true);
            }

            if (clickInfo.current) {
                const { clickPoint, dir } = clickInfo.current;
                const [min, max] = translationLimits?.[axis] || [undefined, undefined];

                let offset = calculateOffset(clickPoint, dir, e.ray.origin, e.ray.direction);
                if (min !== undefined) {
                    offset = Math.max(offset, min - offset0.current);
                }
                if (max !== undefined) {
                    offset = Math.min(offset, max - offset0.current);
                }

                translationRef.current[axis] = offset0.current + offset;
                if (annotations) {
                    divRef.current.innerText = `${translationRef.current[axis].toFixed(2)}`;
                }
                offsetMatrix.makeTranslation(dir.x * offset, dir.y * offset, dir.z * offset);
                onDrag(offsetMatrix);
            }
        },
        [gl.domElement.style, isHovered, translationLimits, axis, annotations, onDrag]
    );

    const onPointerUp = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            if (annotations) {
                divRef.current.style.display = 'none';
            }
            e.stopPropagation();
            clickInfo.current = null;
            onDragEnd();
            camControls && camControlsRef.current && (camControlsRef.current.enabled = true);

            // @ts-expect-error - releasePointerCapture & PointerEvent#pointerId is not in the type definition
            e.target.releasePointerCapture(e.pointerId);
        },
        [annotations, camControls, onDragEnd]
    );

    const onPointerOut = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            e.stopPropagation();
            setIsHovered(false);
            gl.domElement.style.setProperty('cursor', 'auto');
        },
        [gl.domElement.style]
    );

    const { cylinderLength, coneWidth, coneLength, matrixL } = useMemo(() => {
        const coneWidth = fixed ? (lineWidth / scale) * 1.6 : scale / 20;
        const coneLength = fixed ? 0.2 : scale / 5;
        const cylinderLength = fixed ? 1 - coneLength : scale - coneLength;
        const quaternion = new Quaternion().setFromUnitVectors(upV, direction.clone().normalize());
        const matrixL = new Matrix4().makeRotationFromQuaternion(quaternion);
        return { cylinderLength, coneWidth, coneLength, matrixL };
    }, [direction, scale, lineWidth, fixed]);

    return (
        <group ref={objRef} userData={{ ...userData, isPivotControl: true }}>
            <group
                matrix={matrixL}
                matrixAutoUpdate={false}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerOut={onPointerOut}
                onPointerUp={onPointerUp}
            >
                {annotations && (
                    <Html position={[0, -coneLength, 0]}>
                        <div
                            ref={divRef}
                            className={annotationsClass}
                            style={{
                                display: 'none',
                                background: '#151520',
                                color: 'white',
                                padding: '6px 8px',
                                borderRadius: 7,
                                whiteSpace: 'nowrap'
                            }}
                        />
                    </Html>
                )}

                {/* The invisible mesh being raycast */}
                <mesh position={[0, (cylinderLength + coneLength) / 2.0, 0]} userData={userData} visible={false}>
                    <cylinderGeometry args={[coneWidth * 1.4, coneWidth * 1.4, cylinderLength + coneLength, 8, 1]} />
                </mesh>

                {/* The visible mesh */}
                <Line
                    color={color}
                    depthTest={depthTest}
                    fog={false}
                    lineWidth={lineWidth}
                    opacity={opacity}
                    points={[0, 0, 0, 0, cylinderLength, 0]}
                    polygonOffsetFactor={-10}
                    raycast={() => null}
                    renderOrder={renderOrder}
                    side={DoubleSide}
                    polygonOffset
                    transparent
                />
                <mesh
                    position={[0, cylinderLength + coneLength / 2.0, 0]}
                    raycast={() => null}
                    renderOrder={renderOrder}
                >
                    <coneGeometry args={[coneWidth, coneLength, 24, 1]} />
                    <meshBasicMaterial
                        color={color}
                        depthTest={depthTest}
                        fog={false}
                        opacity={opacity}
                        polygonOffsetFactor={-10}
                        polygonOffset
                        transparent
                    />
                </mesh>
            </group>
        </group>
    );
};
